import { ModalService } from '../../../widgets/entry-components/modal/services/modal.service';
import { Component, Input, OnInit } from '@angular/core';
import { ActionSheetController } from '@ionic/angular';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { TranslatePipe } from '../../../shared/translate/pipes/translate.pipe';
import { FormGenModel } from '../../../shared/models/form-gen.model';
import { IPatient } from '@ahryman40k/ts-fhir-types/lib/R4';
import { AbstractAddWmodalComponent } from '../../../shared/abstracts/add/abstract-add-wmodal/abstract-add-wmodal.component';
import { AlertService } from '../../../widgets/entry-components/alert/services/alert.service';
import { setValidators } from '../../../shared/validators/set-validators';
import { EMAIL_REGEX } from '../../../shared/regular-expressions/regex';

export const GENDER_LIST: FormGenModel[] = [
  { label: 'FEMALE', value: 'FEMALE' },
  { label: 'MALE', value: 'MALE' },
  { label: 'OTHER', value: 'OTHER' }
];

export const OTHER_DATAS: string[] = ['address', 'contact', 'telecom'];

@Component({
  selector: 'inc-patient-add',
  templateUrl: './patient-add.component.html',
  styleUrls: ['./patient-add.component.scss'],
})
export class PatientAddComponent extends AbstractAddWmodalComponent<IPatient> implements OnInit {
  genderList = GENDER_LIST;
  hasOtherDatasRequiredModal = false;
  isRequiredField = {};

  @Input() extraValidators?: any;
  @Input() modalParameters?: any;
  @Input() requiredEmail = false;
  @Input() requiredGender = false;
  @Input() datePickerInputStyle = false;

  constructor(
    modalService: ModalService, actionSheetController: ActionSheetController,
    translatePipe: TranslatePipe,
    private alertService: AlertService) {
    super(modalService, actionSheetController, translatePipe);
  }

  ngOnInit() {
    super.ngOnInit();
    if (!this.requiredEmail) {
      this.form.setControl('telecom', new FormArray([]));
    }
    this.discard();
    if (this.extraValidators) {
      this.form = setValidators(this.form, this.extraValidators);
      this.setRedStars(this.extraValidators);
    }
    if (this.requiredGender) {
      this.genderList = GENDER_LIST.filter(gender => gender.value !== 'OTHER');
    }
  }

  resetForm(): FormGroup {
    return new FormGroup({
      id: new FormControl(),
      extension: new FormArray([
        new FormGroup({ valueString: new FormControl('') }),
        new FormGroup({ valueString: new FormControl('') })
      ]),
      identifier: new FormArray([new FormGroup({
        value: new FormControl('', Validators.compose([Validators.required, Validators.minLength(11)]))
      })]),
      active: new FormControl(false),
      name: new FormArray([], Validators.required),
      gender: new FormControl((this.requiredGender) ? '' : 'OTHER', Validators.required),
      telecom: new FormArray([new FormGroup({
        system: new FormControl('EMAIL'),
        value: new FormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
        rank: new FormControl('')
      })]),
      birthDate: new FormControl('', [Validators.required, Validators.minLength(10)]),
      address: new FormArray([]),
      contact: new FormArray([])
    });
  }

  pushIntoFormArray(inData: IPatient, componentName: string, getFormFun?: any) {
    let formArray = this.form.controls[componentName] ? this.form.controls[componentName] as any : new FormArray([]);
    if (inData.hasOwnProperty(componentName)) {
      if (componentName === 'telecom') {
        if (!this.requiredEmail) {
          formArray = new FormArray([]);
        } else {
          if (inData.telecom.length > 0) {
            formArray = new FormArray([]);
            inData = this.checkRequiredEmail(inData);
          }
        }
      }
      for (const data of inData[componentName]) {
        const formGroup = getFormFun();
        formGroup.patchValue(data);
        formArray.push(formGroup);
      }
      if (this.requiredEmail && componentName === 'telecom') {
        if (formArray.at(0)) {
          formArray.at(0).controls.value.setValidators([Validators.required, Validators.pattern(EMAIL_REGEX)]);
        }
      }
    }
    this.form.setControl(componentName, formArray);
  }

  modifyName(index) {
    this.modalService.presentModal(this.modalComponents['name'](), { parameter: this.form.value.name[index] }).then(modal => {
      modal.onDidDismiss().then(detail => {
        if (detail.data) {
          this.form.controls.name.patchValue([detail.data.value.name.value]);
          this.form.updateValueAndValidity();
          this.form.markAsDirty();
        }
      });
    });
  }

  setRedStars(extraValidators: any, path?: string) {
    Object.keys(extraValidators).forEach(key => {
      const newPath = path ? path + '.' + key : key;
      if (Array.isArray(extraValidators[key])) {
        if (extraValidators[key].includes(Validators.required)) {
          if (OTHER_DATAS.includes(key)) {
            this.hasOtherDatasRequiredModal = true;
          } else {
            this.isRequiredField[newPath] = true;
          }
        }
      } else {
        this.setRedStars(extraValidators[key], newPath);
      }
    });
  }

  openModal(componentName: string, param?: any) {
    this.modalService.presentModal(this.modalComponents[componentName](), param).then(modal => {
      modal.onDidDismiss().then(detail => {
        if (detail.data) {
          if (detail.data.value.hasOwnProperty(componentName)) {
            const formArray =
              this.form.controls[componentName] ? this.form.controls[componentName] as any : new FormArray([]);
            formArray.push(detail.data.value[componentName]);
            this.form.setControl(componentName, formArray);
            this.form.updateValueAndValidity();
            this.form.markAsDirty();
          }
        }
      });
    });
  }

  async addDetails() {
    const actionSheet = await this.actionSheetController.create({
      header: this.translatePipe.transform('ADDOTHERDATAS'),
      buttons: [
        {
          text: this.translatePipe.transform('ADDRESS'),
          icon: 'business',
          cssClass: this.handleCssClass('address'),
          handler: () => {
            this.openModal('address', this.handleModalParameters('address'));
          }
        }, {
          text: this.translatePipe.transform('AVAILABILITY'),
          icon: 'call',
          cssClass: this.handleCssClass('telecom'),
          handler: () => {
            this.openModal('telecom', this.handleModalParameters('telecom'));
          }
        }, {
          text: this.translatePipe.transform('CONTACT_PERSON'),
          icon: 'contact',
          cssClass: this.handleCssClass('contact'),
          handler: () => {
            this.openModal('contact', this.handleModalParameters('contact'));
          }
        }, {
          text: this.translatePipe.transform('BACK'),
          icon: 'close',
          role: 'cancel'
        }]
    });
    await actionSheet.present();
  }

  save(inData) {
    if (this.inData) {
      this.inData = inData;
    }
    super.save(inData);
  }

  handleModalParameters(type: string): any {
    if (this.modalParameters && this.modalParameters[type]) {
      return { extraValidators: this.modalParameters[type] };
    }
    return null;
  }

  handleCssClass(type: string): string {
    if (this.extraValidators && this.extraValidators[type] && this.extraValidators[type].includes(Validators.required)) {
      return 'required-action-button';
    }
    return '';
  }

  isRequired(key: string): boolean {
    if (this.isRequiredField[key]) {
      return true;
    }
  }

  checkRequiredEmail(data: any) {
    if (data.telecom[0]) {
      if (data.telecom[0].system && data.telecom[0].system.toLowerCase() !== 'email') {
        const tempFirst = data.telecom[0];
        let tempEmail;
        for (let i = 0; i < data.telecom.length; i++) {
          if (!tempEmail && data.telecom[i].system && data.telecom[i].system.toLowerCase() === 'email') {
            tempEmail = data.telecom[i];
            data.telecom[i] = tempFirst;
            data.telecom[0] = tempEmail;
          }
        }
        if (!tempEmail) {
          data.pop({ system: '', value: '', rank: '' });
        }
      }
    }
    return data;
  }
}
